import { FunctionalComponent, h } from '@stencil/core';
import { FluidFunctionalComponentProps } from '../model/functional/fluid-functional-component-props.interface';
import {
  FluidElementType,
  FluidIconKey,
  FluidLinkTarget,
  FluidMenuItem,
  FluidMenuType,
  FluidSize,
  FluidTextOverflow,
  FluidTheme,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FluidI18nStore } from '../providers/fluid-i18n-provider/store/fluid-i18n.store';

interface NavProps extends FluidFunctionalComponentProps {
  menuItem?: FluidMenuItem;
  theme?: FluidTheme;
  dropdownIcon?: boolean;
  titleAttr?: boolean;
  menuType?: FluidMenuType;

  onClick?: (event: MouseEvent) => void;
  onFocus?: (event: FocusEvent) => void;
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;

  i18nStore?: FluidI18nStore;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Nav - Primary <nav> container & parent <ul>
 */
export const Nav: FunctionalComponent<NavProps> = (
  props: NavProps,
  children
) => {
  return (
    <nav
      {...props}
      class={{
        'fluid-navigation': true,
        [`${props.class}`]: !!props.class,
      }}>
      <ul class='fluid-nav-list'>{children}</ul>
    </nav>
  );
};

/**
 * NavItem - <li> wrapper for all NavButtons & NavSubmenuBtns
 */
export const NavItem: FunctionalComponent<NavProps> = (
  props: NavProps,
  children
) => {
  return (
    <li
      {...props}
      class={{
        'fluid-nav-item': true,
        [`${props.class}`]: !!props.class,
      }}>
      {children}
    </li>
  );
};

/**
 * NavButton - standard "link" button
 * If the FluidMenuItem:
 * 1) uses the "href" property OR
 * 2) uses the "route" property AND has "elementType: a"
 * then the NavButton will be rendered as an <a> tag.
 * Otherwise, the NavButton will be rendered as a <button> tag.
 */
export const NavButton: FunctionalComponent<NavProps> = (props: NavProps) => {
  const { menuItem, theme, titleAttr, menuType, i18nStore, ...rest } = props;

  const MenuItemTag = getMenuItemTag(menuItem);
  const isAnchor = MenuItemTag === FluidElementType.ANCHOR;

  return (
    <MenuItemTag
      {...rest}
      {...(isAnchor && { href: menuItem.href ?? menuItem.route })}
      {...(isAnchor && { target: menuItem.target ?? FluidLinkTarget.SELF })}
      {...(!isAnchor && { disabled: menuItem.disabled })}
      id={menuItem.id}
      tabIndex={menuItem.disabled && -1}
      aria-disabled={String(!!menuItem.disabled)}
      aria-current={String(!!menuItem.selected)}
      class={{
        'fluid-nav-button': true,
        [`${props.class}`]: !!props.class,
        'button-selected':
          menuItem.selected && menuType !== FluidMenuType.DROPDOWN,
        'button-disabled': menuItem.disabled,
      }}>
      <NavIcon position='before' {...props} />
      <NavLabel {...props} i18nStore={i18nStore} />
      <NavBadge {...props} i18nStore={i18nStore} />
      <NavIcon position='after' {...props} />
    </MenuItemTag>
  );
};

/**
 * NavSubmenuBtn - trigger that hides/shows NavSubmenus on click.
 * NOTE: "dropdownIcon" destructured to prevent it from being
 * applied as an attribute to the <button> element (via "...rest").
 */
export const NavSubmenuBtn: FunctionalComponent<NavProps> = (
  props: NavProps
) => {
  const { menuItem, theme, titleAttr, dropdownIcon, menuType, ...rest } = props;

  return (
    <button
      {...rest}
      id={menuItem.id}
      disabled={menuItem.disabled}
      aria-disabled={String(!!menuItem.disabled)}
      aria-current={String(!!menuItem.selected)}
      aria-expanded={String(!!menuItem.submenuOpen)}
      class={{
        'fluid-nav-submenu-button': true,
        [`${props.class}`]: !!props.class,
        'button-disabled': menuItem.disabled,
        'submenu-active': menuItem.submenuOpen,
      }}>
      <NavIcon position='before' {...props} />
      <NavLabel {...props} />
      <NavBadge {...props} />
      <NavIcon position='after' {...props} />
      <NavSubmenuIcon {...props} />
    </button>
  );
};

/**
 * NavSubmenu - <ul> wrapper for submenu items
 */
export const NavSubmenu: FunctionalComponent<NavProps> = (
  props: NavProps,
  children
) => {
  const { menuItem, ...rest } = props;

  return (
    <ul
      {...rest}
      style={{ '--submenu-depth': `${menuItem._submenuDepth}` }}
      class={{
        'fluid-nav-submenu': true,
        [`${props.class}`]: !!props.class,
        'submenu-open': menuItem.submenuOpen,
        'submenu-closed': !menuItem.submenuOpen,
      }}>
      {children}
    </ul>
  );
};

/**
 * NavTitle - inline heading/title for sections within Nav
 */
export const NavTitle: FunctionalComponent<NavProps> = (props: NavProps) => {
  const { menuItem, ...rest } = props;

  return (
    <div
      {...rest}
      class={{
        'fluid-nav-title': true,
        [`${props.class}`]: !!props.class,
      }}>
      <NavLabel {...props} />
    </div>
  );
};

/**
 * NavDivider - <hr /> divider to delineate Nav sections
 */
export const NavDivider: FunctionalComponent<NavProps> = (props: NavProps) => {
  return (
    <div
      {...props}
      class={{
        'fluid-nav-divider': true,
        [`${props.class}`]: !!props.class,
      }}>
      <hr />
    </div>
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * NavLabel - Menu Item Label
 */
const NavLabel: FunctionalComponent<NavProps> = (props: NavProps) => {
  const { menuItem, titleAttr, i18nStore } = props;
  return (
    <span
      {...(titleAttr && { title: menuItem.label })}
      class={{
        'fluid-nav-label': true,
        'text-overflow-wrap': menuItem.textOverflow === FluidTextOverflow.WRAP,
        'text-overflow-clip': menuItem.textOverflow === FluidTextOverflow.CLIP,
        'text-overflow-ellipsis':
          menuItem.textOverflow === FluidTextOverflow.ELLIPSIS,
      }}>
      {i18nStore?.translate(menuItem.fluidTranslate?.label) || menuItem.label}
    </span>
  );
};

/**
 * NavIcon - Menu Item Icon (if provided)
 */
const NavIcon: FunctionalComponent<NavProps> = (props: NavProps) => {
  const { menuItem, menuType, position, theme } = props;

  const iconConfig =
    position === 'before'
      ? menuItem.iconBefore
      : menuItem.iconAfter ?? menuItem.icon;
  if (!iconConfig) return;

  return (
    <fluid-icon
      key={`${menuItem.label}-icon-${position}--${iconConfig.key}`}
      class={{
        [`fluid-nav-icon icon-${position}`]: true,
        'icon-interactive': !!iconConfig.popover,
      }}
      config={{
        size: FluidSize.SMALL,
        ...iconConfig,
        popoverIgnoreClippingBoundaries: !(menuType === FluidMenuType.DROPDOWN),
        disabled: menuItem.disabled,
        overrideGlobalTheme: true,
        theme,
      }}>
      {!!iconConfig.slotName && (
        <slot name={iconConfig.slotName} slot={iconConfig.slotName} />
      )}
    </fluid-icon>
  );
};

/**
 * NavSubmenuIcon - Expand/Collapse Icon
 */
const NavSubmenuIcon: FunctionalComponent<NavProps> = (props: NavProps) => {
  const { menuItem, theme, dropdownIcon } = props;

  if (
    !isNullOrUndefined(menuItem.submenuCollapsible) &&
    !menuItem.submenuCollapsible
  )
    return;

  const iconExpand = !dropdownIcon
    ? FluidIconKey.EXPAND
    : FluidIconKey.CHEVRON_DOWN;
  const iconCollapse = !dropdownIcon
    ? FluidIconKey.COLLAPSE
    : FluidIconKey.CHEVRON_UP;

  return (
    <fluid-icon
      class='fluid-nav-submenu-icon'
      key={`${menuItem.label}-submenu-icon`}
      iconKey={menuItem.submenuOpen ? iconCollapse : iconExpand}
      size={FluidSize.SMALL}
      theme={theme}
      overrideGlobalTheme={true}
      inline={true}
    />
  );
};

/**
 * NavBadge - Menu Item Badge (if provided)
 */
const NavBadge: FunctionalComponent<NavProps> = (props: NavProps) => {
  const { menuItem, theme } = props;

  if (!menuItem.badge) return;

  return (
    <fluid-badge
      class='fluid-nav-badge'
      key={`${menuItem.label}-badge`}
      config={{
        ...menuItem.badge,
        indicator: menuItem.badge.indicator ?? true, // Indicator style by default
        iconBefore: !!menuItem.badge.iconBefore && {
          ...menuItem.badge.iconBefore,
          size: FluidSize.EXTRA_SMALL,
        },
        iconAfter: !!menuItem.badge.iconAfter && {
          ...menuItem.badge.iconAfter,
          size: FluidSize.EXTRA_SMALL,
        },
        overrideGlobalTheme: true,
        theme,
      }}
    />
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Returns appropriate HTML tag for a menu item based on whether it
 * should be a link or button (accessibility/usability best practice).
 * @param menuItem - the menu item to be rendered
 */
const getMenuItemTag = (menuItem: FluidMenuItem): FluidElementType => {
  switch (true) {
    case !!menuItem.href:
    case !!menuItem.route && menuItem.elementType === FluidElementType.ANCHOR:
      return FluidElementType.ANCHOR;
    default:
      return FluidElementType.BUTTON;
  }
};
